// src/context/AuthContext.js
// The signed-in user. The session itself is an HttpOnly cookie the page cannot
// read (services/api.js); on load we ask the API who is signed in. Nothing about
// the session or the user is kept in localStorage.
import React, { createContext, useState, useEffect, useCallback } from 'react';
import { authAPI, kioskAPI, setSessionEndedHandler } from '../services/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore the session from the cookie when the app starts.
  useEffect(() => {
    setSessionEndedHandler(() => setUser(null));
    let cancelled = false;
    authAPI.session()
      .then(({ data }) => { if (!cancelled) setUser(data.user); })
      .catch(() => { if (!cancelled) setUser(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    setUser(data.user); // data.user contains role from backend
  };

  const register = async (name, email, password, phone) => {
    const { data } = await authAPI.register({ name, email, password, phone });
    setUser(data.user);
  };

  // tokens: { idToken } for Google/Apple, { accessToken } for Facebook.
  // The provider token is verified server-side; we never send a self-asserted identity.
  const socialLogin = async (provider, tokens) => {
    const { data } = await authAPI.socialLogin({ provider, ...tokens });
    setUser(data.user);
  };

  // The API already set the session cookie (e.g. the email confirmation link).
  const completeSignIn = useCallback((signedInUser) => setUser(signedInUser), []);

  // Kiosks cannot sign out here (the API refuses); they use exitKiosk.
  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      if (err.response?.data?.code === 'KIOSK_PASSWORD_REQUIRED') throw err;
      // Otherwise the session is gone either way.
    }
    setUser(null);
  }, []);

  // Leave kiosk mode: the kiosk password is checked by the API.
  const exitKiosk = async (password) => {
    await kioskAPI.exit(password);
    setUser(null);
  };

  // Update user data in context (e.g. after editing the profile)
  const updateUser = (updatedData) => {
    setUser((current) => ({ ...current, ...updatedData }));
  };

  return (
    <AuthContext.Provider value={{ user, login, register, socialLogin, completeSignIn, logout, exitKiosk, loading, updateUser }}>
      {loading ? (
        <div className="flex items-center justify-center min-h-screen text-sm text-app-text-secondary-light dark:text-app-text-secondary">
          Loading...
        </div>
      ) : children}
    </AuthContext.Provider>
  );
};
